import { faMinusCircle, faPlusCircle } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FieldArray, useFormikContext } from 'formik';
import type { FC } from 'react';
import { useEffect, useMemo } from 'react';
import { v4 as uuid } from 'uuid';
import type { Link } from 'src/apis/api-utilities';
import {
  initialValuesByProductType,
  productTypeOptions,
  SBU_UNIT,
  sbuUnitOption,
  storageAllocationMethodOptions,
  storageCapacityCategoryOptions,
  storageCommodityUnitOptions,
  storageFlowUnitOptions,
  storageProductPeriodOptions,
} from 'src/apis/storage/mappings';
import type { StorageLocation } from 'src/apis/storage/types';
import type { PublicStorageLocationsRequest } from 'src/apis/storage/use-public-storage-locations';
import { Button } from 'src/components/buttons-and-actions/button';
import { Card } from 'src/components/data-display/card';
import { EmptyValue } from 'src/components/data-display/empty-value';
import { DatePicker } from 'src/components/form/datepicker/date-picker';
import { DateRangePicker } from 'src/components/form/datepicker/date-range-picker';
import { Editor } from 'src/components/form/editor';
import { FieldGroup } from 'src/components/form/field-group';
import { formGapClasses } from 'src/components/form/form-container';
import { Input } from 'src/components/form/input';
import { Select } from 'src/components/form/select';
import { SearchableSingleSelect } from 'src/components/form/select/searchable-single-select';
import { Spacer } from 'src/components/layout/spacer';
import { Heading } from 'src/components/text/heading';
import type { Successful } from 'src/hooks/use-axios';
import { useClientSearch } from 'src/hooks/use-client-search';
import { useNow } from 'src/hooks/use-now';
import type {
  StorageOfferFormValues,
  ProductComponentSchema,
} from 'src/pages/storage/offers-new/create/schema';
import { AttachFilesFields } from 'src/pages/storage/offers-new/create/upload-files-fields';
import { cn } from 'src/utils/cn';

type Props = {
  storageLocations: Successful<PublicStorageLocationsRequest>;
  prepareFileUploadLink: Link;
};

export const StorageOfferFields: FC<Props> = ({
  storageLocations,
  prepareFileUploadLink,
}) => {
  const now = useNow();

  const { values, setFieldValue } = useFormikContext<StorageOfferFormValues>();

  const locationsList: StorageLocation[] =
    storageLocations.response.data._embedded.items;

  const locationOptions = useMemo(
    () =>
      locationsList.map((loc) => ({
        label: loc.locationName,
        value: loc.locationId,
      })),
    [locationsList]
  );

  const locationsSearch = useClientSearch(locationOptions, (opt, search) =>
    opt.label.toLowerCase().match(search)
  );

  const productType = values.productType;
  const typeOfProductUnitOptions = useMemo(() => {
    if (productType === SBU_UNIT) return sbuUnitOption;
    if (productType === 'storageVolume') return storageCommodityUnitOptions;
    return storageFlowUnitOptions;
  }, [productType]);

  const quantityUnitLabel = typeOfProductUnitOptions.find(
    (option) => option.value === values.typeOfProductUnit
  )?.label;

  useEffect(() => {
    setFieldValue(
      'typeOfProductUnit',
      initialValuesByProductType[productType].typeOfProductUnit
    );
  }, [productType, setFieldValue]);

  return (
    <>
      <Heading data-testid="general-settings-section" mode="title" size="md">
        General Settings
      </Heading>
      <Input
        name="name"
        label="Offer Name"
        placeholder="E.g. North Storage Zone 3"
        markAsRequired
      />
      <SearchableSingleSelect
        name="storageLocationId"
        label="Storage Location"
        placeholder="E.g. North Location 3"
        markAsRequired
        {...locationsSearch}
      />
      <Select
        name="productPeriod"
        label="Product Period"
        placeholder="E.g. Day"
        options={[
          { label: 'Please Select', value: null },
          ...storageProductPeriodOptions,
        ]}
        markAsRequired
      />

      {/* Date fields in chain order: publication → bidding window → product runtime */}
      <DatePicker
        name="publication"
        label="Offer Publication"
        time
        markAsRequired
        minBookingDate={new Date(now)}
      />
      <DateRangePicker
        groupLabel="Bidding Window Duration*"
        fromName="biddingWindowStart"
        fromLabel="Start"
        toName="biddingWindowEnd"
        toLabel="End"
        time
        fromMarkAsRequired
        toMarkAsRequired
        minBookingDate={new Date(now)}
      />
      <DateRangePicker
        groupLabel="Product Runtime*"
        fromName="productRuntimeStart"
        fromLabel="Start"
        toName="productRuntimeEnd"
        toLabel="End"
        time
        fromMarkAsRequired
        toMarkAsRequired
        minBookingDate={new Date(now)}
      />

      {/* 
        As BE not able to provide "productRuntimeEnd" as a label in the violation message, we need to include it as a hidden input here.
        Usually this should never be triggered as we validate the product runtime in the schema before submitting.
        However, we still include it for in case we received 400 error from the API.
      */}
      <Input
        name="productRuntime.end"
        label="Product Runtime End"
        type="hidden"
        hideLabel
      />

      <FieldGroup label="Type of Product and Unit*">
        <Select
          name="productType"
          label="Type of Product"
          options={productTypeOptions}
          markAsRequired
        />
        <Select
          name="typeOfProductUnit"
          label="Unit"
          options={
            productType === 'SBU' ? sbuUnitOption : typeOfProductUnitOptions
          }
          markAsRequired
        />
      </FieldGroup>

      <Select
        name="allocationMethod"
        label="Allocation Method"
        options={storageAllocationMethodOptions}
        markAsRequired
      />
      <Spacer />

      <Heading data-testid="product-settings-section" mode="title" size="md">
        Product Settings
      </Heading>
      <Input
        name="productName"
        label="Product Name"
        placeholder="E.g. North 2026/2027 - Firm"
        markAsRequired
      />
      <Input
        name="availableQuantity"
        label="Available Quantity"
        type="number"
        min={1}
        placeholder="E.g. 40"
        unit={quantityUnitLabel}
        markAsRequired
      />
      <Input
        name="reservePrice"
        label="Reserve Price"
        type="number"
        min={0}
        unit={`EUR/${quantityUnitLabel}/Runtime`}
        placeholder="E.g. 10"
      />
      <Spacer />

      <Heading data-testid="product-components-section" mode="title" size="md">
        Product Components
      </Heading>
      <FieldArray
        name="productComponents"
        render={(arrayHelpers) => {
          const productComponents =
            values.productComponents as ProductComponentSchema[];
          const emptyEntry: ProductComponentSchema = {
            id: uuid(),
            capacityCategory: null,
            runtimeStart: null,
            runtimeEnd: null,
            storageVolumeAmount: null,
            storageVolumeUnit: null,
            injectionCapacityAmount: null,
            injectionCapacityUnit: null,
            withdrawalCapacityAmount: null,
            withdrawalCapacityUnit: null,
          };
          return (
            <div className={cn('grid', formGapClasses)}>
              {productComponents.length === 0 ? (
                <>
                  <Card mode="highlight">
                    <div
                      className={`
                        grid justify-items-center gap-xs
                        legacy:gap-[1rem]
                      `}
                    >
                      <EmptyValue label="No Product Components added yet" />
                    </div>
                  </Card>
                  <div className="flex justify-end">
                    <Button
                      mode="link"
                      onClick={() => arrayHelpers.push(emptyEntry)}
                      data-testid="add-product-component"
                    >
                      <FontAwesomeIcon icon={faPlusCircle} className="mr-xs" />
                      Add Product Component
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  {productComponents.map((productComponent, index) => (
                    <ProductComponentRow
                      key={productComponent.id}
                      index={index}
                      onRemove={() => arrayHelpers.remove(index)}
                      canRemove={productComponents.length > 0}
                      now={new Date(now)}
                    />
                  ))}
                  <div className="flex justify-end">
                    <Button
                      mode="link"
                      onClick={() =>
                        arrayHelpers.push({ ...emptyEntry, id: uuid() })
                      }
                      data-testid="add-product-component"
                    >
                      <FontAwesomeIcon icon={faPlusCircle} className="mr-xs" />
                      Add More
                    </Button>
                  </div>
                </>
              )}
            </div>
          );
        }}
      />
      <Spacer />

      <Heading
        data-testid="additional-information-section"
        mode="title"
        size="md"
      >
        Additional Information
      </Heading>
      <Editor name="offerSpecificNote" label="Offer Specific Note" />
      <AttachFilesFields prepareFileUploadLink={prepareFileUploadLink} />
    </>
  );
};

const ProductComponentRow: FC<{
  index: number;
  onRemove: () => void;
  canRemove: boolean;
  now: Date;
}> = ({ index, onRemove, canRemove, now }) => {
  const { values } = useFormikContext<StorageOfferFormValues>();

  const productComponents =
    values.productComponents as ProductComponentSchema[];
  const productComponent = productComponents[index];

  const storageVolumeAndUnitMandatory =
    productComponent.storageVolumeAmount != null ||
    productComponent.storageVolumeUnit != null;
  const injectionCapacityAndUnitMandatory =
    productComponent.injectionCapacityAmount != null ||
    productComponent.injectionCapacityUnit != null;
  const withdrawalCapacityAndUnitMandatory =
    productComponent.withdrawalCapacityAmount != null ||
    productComponent.withdrawalCapacityUnit != null;

  return (
    <Card mode="highlight" data-testid={`product-component-row-${index}`}>
      <div className="grid grid-flow-col justify-between">
        <Heading mode="title" size="md">
          Product Component {index + 1}
        </Heading>
        {canRemove && (
          <Button
            mode="link"
            onClick={onRemove}
            data-testid={`remove-product-component-${index}`}
          >
            <FontAwesomeIcon icon={faMinusCircle} /> Remove
          </Button>
        )}
      </div>
      <div className={cn('mt-md grid gap-y-md', formGapClasses)}>
        <Select
          name={`productComponents[${index}].capacityCategory`}
          label="Capacity Category"
          options={storageCapacityCategoryOptions}
          placeholder="E.g. Firm"
          markAsRequired
        />
        <DateRangePicker
          groupLabel="Runtime*"
          fromName={`productComponents[${index}].runtimeStart`}
          fromLabel="Start"
          toName={`productComponents[${index}].runtimeEnd`}
          toLabel="End"
          time
          fromMarkAsRequired
          toMarkAsRequired
          minBookingDate={new Date(now)}
        />
        <FieldGroup
          label={
            storageVolumeAndUnitMandatory
              ? 'Storage Volume and Unit*'
              : 'Storage Volume and Unit'
          }
        >
          <Input
            name={`productComponents[${index}].storageVolumeAmount`}
            label="Storage Volume"
            type="number"
            min={0}
            placeholder="E.g. 10"
          />
          <Select
            name={`productComponents[${index}].storageVolumeUnit`}
            label="Unit"
            options={[
              { label: 'Please Select', value: null },
              ...storageCommodityUnitOptions,
            ]}
            placeholder="kWh"
          />
        </FieldGroup>
        <FieldGroup
          label={
            injectionCapacityAndUnitMandatory
              ? 'Injection Capacity and Unit*'
              : 'Injection Capacity and Unit'
          }
        >
          <Input
            name={`productComponents[${index}].injectionCapacityAmount`}
            label="Injection Capacity"
            type="number"
            min={0}
            placeholder="E.g. 23"
          />
          <Select
            name={`productComponents[${index}].injectionCapacityUnit`}
            label="Unit"
            options={[
              { label: 'Please Select', value: null },
              ...storageFlowUnitOptions,
            ]}
            placeholder="kWh/h"
          />
        </FieldGroup>
        <FieldGroup
          label={
            withdrawalCapacityAndUnitMandatory
              ? 'Withdrawal Capacity and Unit*'
              : 'Withdrawal Capacity and Unit'
          }
        >
          <Input
            name={`productComponents[${index}].withdrawalCapacityAmount`}
            label="Withdrawal Capacity"
            type="number"
            min={0}
            placeholder="E.g. 23"
          />
          <Select
            name={`productComponents[${index}].withdrawalCapacityUnit`}
            label="Unit"
            options={[
              { label: 'Please Select', value: null },
              ...storageFlowUnitOptions,
            ]}
            placeholder="kWh/h"
          />
        </FieldGroup>
      </div>
    </Card>
  );
};
